/**
 * SocialPostEditor Component
 *
 * Create/edit social media post with caption, assets, platform selection,
 * and approval settings. Features auto-save for drafts.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Send,
  Clock,
  Loader2,
  Hash,
  X,
  Plus,
  Eye,
  Pencil,
  Calendar,
  Check,
  Cloud,
  Facebook,
  Instagram,
  Music2,
  Users,
  CheckCircle,
} from 'lucide-react';

import SocialAssetUploader from './SocialAssetUploader';
import SocialPostPreview from './SocialPostPreview';
import PostEngagementPanel from './PostEngagementPanel';
import { SocialPostEditorSkeleton } from './SocialPostSkeleton';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { social } from '@/services/api/admin';

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-blue-600' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-600' },
  { id: 'tiktok', label: 'TikTok', icon: Music2, color: 'text-gray-900 dark:text-white' },
];

const SocialPostEditor = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = Boolean(postId);

  // Form state
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [targetPlatforms, setTargetPlatforms] = useState([]);
  const [platformCaptions, setPlatformCaptions] = useState({});
  const [assets, setAssets] = useState([]);
  const [requiredApprovals, setRequiredApprovals] = useState(1);
  const [autoApproveEnabled, setAutoApproveEnabled] = useState(true);
  const [scheduledFor, setScheduledFor] = useState('');

  // UI state
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewPopoverOpen, setReviewPopoverOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [compatibilityIssues, setCompatibilityIssues] = useState({});

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  const hasChangesRef = useRef(false);

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  // Load existing post
  useEffect(() => {
    if (postId) {
      loadPost();
    }
  }, [postId]);

  const loadPost = async () => {
    setIsLoading(true);
    try {
      const data = await social.getPost(postId);
      setPost(data.post);
      setTitle(data.post.title || '');
      setCaption(data.post.caption || '');
      setHashtags(data.post.hashtags || []);
      setTargetPlatforms(data.post.target_platforms || []);
      setPlatformCaptions(data.post.platform_captions || {});
      setRequiredApprovals(data.post.required_approvals || 1);
      setAutoApproveEnabled(data.post.auto_approve_enabled !== false);
      setAssets(data.assets || []);
      if (data.post.scheduled_for) {
        setScheduledFor(new Date(data.post.scheduled_for).toISOString().slice(0, 16));
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to load post',
        variant: 'destructive',
      });
      navigate('/admin/outreach/social');
    } finally {
      setIsLoading(false);
    }
  };

  // Check compatibility when platforms or assets change
  useEffect(() => {
    const issues = {};
    targetPlatforms.forEach((platform) => {
      const platformIssues = [];
      assets.forEach((asset) => {
        const assetIssues = asset.compatibility_issues?.[platform] || [];
        platformIssues.push(...assetIssues);
      });
      if (platformIssues.length > 0) {
        issues[platform] = [...new Set(platformIssues)];
      }
    });
    setCompatibilityIssues(issues);
  }, [targetPlatforms, assets]);

  // Auto-save function
  const performAutoSave = useCallback(async () => {
    if (!isEditing || !post || post.status !== 'draft' || !hasChangesRef.current) {
      return;
    }

    setAutoSaveStatus('saving');
    try {
      const postData = {
        title,
        caption,
        hashtags,
        target_platforms: targetPlatforms,
        platform_captions: platformCaptions,
        asset_ids: assets.map((a) => a.id),
        required_approvals: requiredApprovals,
        auto_approve_enabled: autoApproveEnabled,
      };

      await social.updatePost(postId, postData);
      setAutoSaveStatus('saved');
      setLastSaved(new Date());
      hasChangesRef.current = false;
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('unsaved');
    }
  }, [isEditing, post, postId, title, caption, hashtags, targetPlatforms, platformCaptions, assets, requiredApprovals, autoApproveEnabled]);

  // Trigger auto-save on content changes (debounced)
  useEffect(() => {
    if (!isEditing || !post || post.status !== 'draft') {
      return;
    }

    hasChangesRef.current = true;
    setAutoSaveStatus('unsaved');

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave();
    }, 2000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [title, caption, hashtags, targetPlatforms, platformCaptions, assets, requiredApprovals, autoApproveEnabled, isEditing, post, performAutoSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const handleSave = async () => {
    if (!title.trim()) {
      toast({
        title: 'Missing Title',
        description: 'Please enter a title for internal reference',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const postData = {
        title,
        caption,
        hashtags,
        target_platforms: targetPlatforms,
        platform_captions: platformCaptions,
        asset_ids: assets.map((a) => a.id),
        required_approvals: requiredApprovals,
        auto_approve_enabled: autoApproveEnabled,
      };

      if (isEditing) {
        await social.updatePost(postId, postData);
        toast({ title: 'Post updated' });
        loadPost();
      } else {
        const newPost = await social.createPost(postData);
        toast({ title: 'Post created' });
        navigate(`/admin/outreach/social/${newPost.id}`);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save post',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!isEditing) {
      await handleSave();
    }

    setIsSaving(true);
    try {
      await social.submitForReview(postId);
      toast({ title: 'Submitted for review' });
      loadPost();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    setIsSaving(true);
    try {
      await social.approvePost(postId, { notes: null });
      setReviewPopoverOpen(false);
      loadPost();
      toast({ title: 'Post approved' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setIsSaving(true);
    try {
      await social.rejectPost(postId, { reason: rejectReason });
      setShowRejectDialog(false);
      setReviewPopoverOpen(false);
      setRejectReason('');
      loadPost();
      toast({ title: 'Post rejected' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasUserApproved = post?.approvals?.some(
    (a) => a.admin_email === currentUser.email
  );
  const canApprovePost = post?.status === 'pending_review' && !hasUserApproved;

  const handlePublish = async () => {
    setIsSaving(true);
    try {
      await social.publishPost(postId);
      toast({ title: 'Publishing started' });
      loadPost();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduledFor) return;

    setIsSaving(true);
    try {
      await social.schedulePost(postId, { scheduled_for: new Date(scheduledFor).toISOString() });
      setShowScheduleDialog(false);
      toast({ title: 'Post scheduled' });
      loadPost();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, '');
    if (tag && !hashtags.includes(tag)) {
      setHashtags([...hashtags, tag]);
    }
    setHashtagInput('');
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter((h) => h !== tag));
  };

  const togglePlatform = (platformId) => {
    if (!canEdit) return;
    setTargetPlatforms((prev) =>
      prev.includes(platformId)
        ? prev.filter((p) => p !== platformId)
        : [...prev, platformId]
    );
  };

  const canEdit = !post || ['draft', 'pending_review'].includes(post.status);
  const canSubmit = isEditing && post?.status === 'draft' && targetPlatforms.length > 0;
  const canPublish = isEditing && post?.status === 'approved';
  const canSchedule = isEditing && ['approved', 'scheduled'].includes(post?.status);

  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const seconds = Math.floor((new Date() - lastSaved) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes === 1) return '1 min ago';
    return `${minutes} mins ago`;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      pending_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      published: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return colors[status] || colors.draft;
  };

  if (isLoading) {
    return <SocialPostEditorSkeleton />;
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/outreach/social')}
          className="gap-2 -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Posts
        </Button>

        {/* Control Bar */}
        <div className="flex flex-wrap items-center gap-3 py-3 px-4 bg-gray-50 dark:bg-gray-900 rounded-lg border">
          {/* Status Badge */}
          {post && (
            <Badge className={cn('font-medium', getStatusColor(post.status))}>
              {post.status.replace('_', ' ')}
            </Badge>
          )}
          {!post && (
            <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
              New Post
            </Badge>
          )}

          {/* Auto-save indicator */}
          {isEditing && post?.status === 'draft' && (
            <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              {autoSaveStatus === 'saving' && (
                <>
                  <Cloud className="h-3.5 w-3.5 animate-pulse" />
                  Saving...
                </>
              )}
              {autoSaveStatus === 'saved' && lastSaved && (
                <>
                  <Check className="h-3.5 w-3.5 text-green-500" />
                  Saved {formatLastSaved()}
                </>
              )}
              {autoSaveStatus === 'unsaved' && (
                <>
                  <Cloud className="h-3.5 w-3.5" />
                  Unsaved
                </>
              )}
            </span>
          )}

          <Separator orientation="vertical" className="h-6" />

          {/* Edit/Preview Toggle */}
          <div className="flex items-center bg-white dark:bg-gray-800 rounded-md border p-0.5">
            <button
              onClick={() => setActiveTab('edit')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors',
                activeTab === 'edit'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors',
                activeTab === 'preview'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              <Eye className="h-3.5 w-3.5" />
              Preview
            </button>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* Platform Selection */}
          <div className="flex items-center gap-1">
            {PLATFORMS.map((platform) => {
              const Icon = platform.icon;
              const isSelected = targetPlatforms.includes(platform.id);
              const hasIssues = compatibilityIssues[platform.id]?.length > 0;

              return (
                <Tooltip key={platform.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => togglePlatform(platform.id)}
                      disabled={!canEdit}
                      className={cn(
                        'relative p-2 rounded-md transition-all',
                        isSelected
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800',
                        !canEdit && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {hasIssues && isSelected && (
                        <span className="absolute -top-1 -right-1 h-2 w-2 bg-amber-500 rounded-full" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{platform.label}</p>
                    {hasIssues && (
                      <p className="text-amber-500 text-xs">Has compatibility issues</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* Approval Settings */}
          <div className="flex items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => canEdit && setAutoApproveEnabled(!autoApproveEnabled)}
                  disabled={!canEdit}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1.5 rounded text-sm transition-colors',
                    autoApproveEnabled
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                    !canEdit && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <CheckCircle className="h-3.5 w-3.5" />
                  Auto
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Auto-approve when threshold met</p>
              </TooltipContent>
            </Tooltip>

            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-gray-400" />
              <Select
                value={requiredApprovals.toString()}
                onValueChange={(v) => setRequiredApprovals(parseInt(v))}
                disabled={!canEdit || !autoApproveEnabled}
              >
                <SelectTrigger className="h-8 w-14 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Review Status Indicator (for existing posts) */}
          {isEditing && post && (post.status === 'pending_review' || post.status === 'approved' || post.rejection) && (
            <>
              <Separator orientation="vertical" className="h-6" />
              <Popover open={reviewPopoverOpen} onOpenChange={setReviewPopoverOpen}>
                <PopoverTrigger asChild>
                  <button className={cn(
                    'flex items-center gap-1.5 px-2 py-1.5 rounded text-sm cursor-pointer hover:opacity-80 transition-opacity',
                    post.status === 'approved' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                    post.status === 'pending_review' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                    post.rejection && post.status === 'draft' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  )}>
                    {post.status === 'approved' && (
                      <>
                        <CheckCircle className="h-3.5 w-3.5" />
                        Approved
                      </>
                    )}
                    {post.status === 'pending_review' && (
                      <>
                        <Clock className="h-3.5 w-3.5" />
                        {post.approvals?.length || 0}/{post.required_approvals || 1}
                      </>
                    )}
                    {post.rejection && post.status === 'draft' && (
                      <>
                        <X className="h-3.5 w-3.5" />
                        Rejected
                      </>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" className="w-72 p-3">
                  <div className="space-y-3">
                    <div className="font-medium text-sm">Review Status</div>

                    {/* Approval list */}
                    {post.approvals?.length > 0 && (
                      <div className="space-y-1.5">
                        {post.approvals.map((approval, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                            <span className="truncate">{approval.admin_email}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Already approved message */}
                    {hasUserApproved && post.status === 'pending_review' && (
                      <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
                        <CheckCircle className="h-3.5 w-3.5" />
                        You have approved this post
                      </div>
                    )}

                    {/* Rejection info */}
                    {post.rejection && (
                      <div className="text-xs bg-red-50 dark:bg-red-900/20 p-2 rounded space-y-1">
                        <div className="flex items-center gap-1.5 text-red-700 dark:text-red-300">
                          <X className="h-3.5 w-3.5" />
                          Rejected by {post.rejection.admin_email?.split('@')[0]}
                        </div>
                        <p className="text-red-600 dark:text-red-400">{post.rejection.reason}</p>
                      </div>
                    )}

                    {/* Action buttons */}
                    {post.status === 'pending_review' && (
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={handleApprove}
                          disabled={!canApprovePost || isSaving}
                          className="flex-1 h-8 bg-green-600 hover:bg-green-700"
                        >
                          {isSaving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          )}
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setShowRejectDialog(true)}
                          disabled={isSaving}
                          className="flex-1 h-8"
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button onClick={handleSave} disabled={isSaving} size="sm" variant="outline">
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <Save className="h-4 w-4 mr-1.5" />
                )}
                Save
              </Button>
            )}
            {canSubmit && (
              <Button onClick={handleSubmitForReview} disabled={isSaving} size="sm">
                <Send className="h-4 w-4 mr-1.5" />
                Submit
              </Button>
            )}
            {canSchedule && (
              <Button
                onClick={() => setShowScheduleDialog(true)}
                disabled={isSaving}
                size="sm"
                variant="secondary"
              >
                <Clock className="h-4 w-4 mr-1.5" />
                Schedule
              </Button>
            )}
            {canPublish && (
              <Button onClick={handlePublish} disabled={isSaving} size="sm">
                <Send className="h-4 w-4 mr-1.5" />
                Publish
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-4">
            {activeTab === 'edit' ? (
              <>
                {/* Post Content Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Post Content</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="title" className="text-sm">Title (internal)</Label>
                      <Input
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g., Summer Promotion July 2024"
                        disabled={!canEdit}
                        className="h-9"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="caption" className="text-sm">Caption</Label>
                        <span className="text-xs text-gray-400">{caption.length} chars</span>
                      </div>
                      <Textarea
                        id="caption"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Write your post caption..."
                        rows={6}
                        disabled={!canEdit}
                        className="resize-none"
                      />
                    </div>

                    {/* Hashtags */}
                    <div className="space-y-1.5">
                      <Label className="text-sm">Hashtags</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                          <Input
                            value={hashtagInput}
                            onChange={(e) => setHashtagInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                            placeholder="Add hashtag"
                            className="pl-8 h-9"
                            disabled={!canEdit}
                          />
                        </div>
                        <Button type="button" onClick={addHashtag} disabled={!canEdit} size="sm" variant="secondary">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {hashtags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {hashtags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="gap-1 text-xs">
                              #{tag}
                              {canEdit && (
                                <button onClick={() => removeHashtag(tag)} className="ml-0.5 hover:text-red-500">
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Media Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Media</CardTitle>
                      <span className="text-xs text-gray-400">{assets.length}/10</span>
                    </div>
                    <CardDescription className="text-xs">Add images or videos</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SocialAssetUploader
                      assets={assets}
                      onChange={setAssets}
                      maxAssets={10}
                      disabled={!canEdit}
                    />
                  </CardContent>
                </Card>

                {/* Engagement Panel (published posts only) */}
                {post?.status === 'published' && (
                  <PostEngagementPanel postId={post.id} />
                )}
              </>
            ) : (
              /* Preview Tab */
              <Card>
                <CardContent className="p-6">
                  <SocialPostPreview
                    caption={caption}
                    assets={assets}
                    hashtags={hashtags}
                    targetPlatforms={targetPlatforms}
                    platformCaptions={platformCaptions}
                  />
                </CardContent>
              </Card>
            )}
        </div>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Post</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this post.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Reason</Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Describe what needs to be changed..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={!rejectReason.trim() || isSaving}
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Reject Post
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Schedule Dialog */}
        <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule Post</DialogTitle>
              <DialogDescription>
                Choose when you want this post to be published.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="schedule-datetime">Publish Date & Time</Label>
                <Input
                  id="schedule-datetime"
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSchedule} disabled={!scheduledFor || isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Calendar className="h-4 w-4 mr-2" />
                Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default SocialPostEditor;
